import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

export interface Tab {
  id: string;
  label: string;
  icon?: React.ComponentType<{ className?: string }>;
  content: ReactNode;
}

export interface TabsProps {
  tabs: Tab[];
  defaultTab?: string;
  onChange?: (tabId: string) => void;
  /**
   * When set, the component runs in controlled mode: the parent owns the
   * active-tab state and can programmatically switch tabs (used by the
   * Edge config page to allow the Subscriptions summary to jump into the
   * Logical Interfaces tab). Pair with `onChange` to observe user-initiated
   * switches. Leave undefined for uncontrolled mode (default).
   */
  activeTab?: string;
}

const Tabs = ({ tabs, defaultTab, onChange, activeTab: controlledActiveTab }: TabsProps) => {
  const [uncontrolledActiveTab, setUncontrolledActiveTab] = useState(defaultTab || tabs[0]?.id);
  const activeTab = controlledActiveTab ?? uncontrolledActiveTab;

  // Horizontal-scroll affordances for tab strips that overflow their container
  // (e.g. the Edge config page has 12 tabs). Without this the trailing tabs are
  // clipped by the card edge with no way to reach them. We show gradient fades
  // and chevron buttons only on the side(s) that have hidden content.
  const scrollRef = useRef<HTMLDivElement>(null);
  const activeTabRef = useRef<HTMLButtonElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    // 1px tolerance for sub-pixel rounding so the affordance doesn't linger.
    setCanScrollLeft(scrollLeft > 1);
    setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
  }, []);

  useLayoutEffect(() => {
    updateScrollState();
    const el = scrollRef.current;
    if (!el) return;
    const observer = new ResizeObserver(updateScrollState);
    observer.observe(el);
    return () => observer.disconnect();
  }, [updateScrollState, tabs.length]);

  // Keep the active tab in view when it changes programmatically (deep links,
  // the Subscriptions -> Logical Interfaces jump), so it's never left off-screen.
  useEffect(() => {
    activeTabRef.current?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }, [activeTab]);

  const scrollByStep = (direction: 1 | -1) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: direction * el.clientWidth * 0.7, behavior: 'smooth' });
  };

  const handleTabChange = (tabId: string) => {
    if (controlledActiveTab === undefined) setUncontrolledActiveTab(tabId);
    onChange?.(tabId);
  };

  const activeTabContent = tabs.find((tab) => tab.id === activeTab)?.content;

  return (
    <div className="space-y-4">
      {/* Tab Headers */}
      <div className="relative border-b border-gray-200">
        {/* Left affordance */}
        {canScrollLeft && (
          <>
            <div className="pointer-events-none absolute inset-y-0 left-0 z-10 w-12 bg-gradient-to-r from-white to-transparent" />
            <button
              type="button"
              aria-label="Scroll tabs left"
              onClick={() => scrollByStep(-1)}
              className="absolute left-0 top-1/2 z-20 -translate-y-1/2 rounded-full border border-gray-200 bg-white p-1 text-gray-500 shadow-sm transition-colors hover:text-gray-700"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
          </>
        )}

        <div
          ref={scrollRef}
          onScroll={updateScrollState}
          className="overflow-x-auto scrollbar-none"
        >
          <nav className="flex space-x-8" aria-label="Tabs">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  ref={isActive ? activeTabRef : undefined}
                  onClick={() => handleTabChange(tab.id)}
                  className={`
                    py-3 px-1 inline-flex items-center space-x-2 border-b-2 font-medium text-sm transition-colors whitespace-nowrap
                    ${
                      isActive
                        ? 'border-primary-600 text-primary-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }
                  `}
                >
                  {Icon && <Icon className="w-4 h-4" />}
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </nav>
        </div>

        {/* Right affordance */}
        {canScrollRight && (
          <>
            <div className="pointer-events-none absolute inset-y-0 right-0 z-10 w-12 bg-gradient-to-l from-white to-transparent" />
            <button
              type="button"
              aria-label="Scroll tabs right"
              onClick={() => scrollByStep(1)}
              className="absolute right-0 top-1/2 z-20 -translate-y-1/2 rounded-full border border-gray-200 bg-white p-1 text-gray-500 shadow-sm transition-colors hover:text-gray-700"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </>
        )}
      </div>

      {/* Tab Content */}
      <div className="py-4" key={activeTab}>
        {activeTabContent}
      </div>
    </div>
  );
};

export default Tabs;
